import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataProvider';
import { buildSupportMailto } from '../utils/supportMailto';
import { activateOnEnterOrSpace } from '../utils/a11y';
import { useMediaQuery } from '../hooks/useMediaQuery';
import MobileDrawer from './MobileDrawer';

// Juuni-internal org ID — only this org sees the Phase 2 marketplace admin tools.
// Customer SPLs (e.g. Fred @ Deltra) don't know the marketplace exists yet.
const JUUNI_INTERNAL_ORG_ID = '00000000-0000-0000-0000-000000000099';

export default function AdminSidebar({ pendingAppCount: _pendingAppCount = 0, alertCount: _alertCount = 0 }) {
  const { user, role, isAdmin, signOut, impersonating, stopImpersonating } = useAuth();
  const { organization } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 14.2 — collapse to hamburger drawer at md breakpoint
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Map nested routes to their parent sidebar item
  const getExtraActive = (to) => {
    const path = location.pathname;
    if (to === '/admin/projects') return path.startsWith('/admin/project/') || path.startsWith('/admin/create-project');
    // Annotators sidebar item lights for: per-annotator detail page +
    // legacy /admin/applications URL (which redirects to ?tab=applications).
    if (to === '/admin/workforce') return path.startsWith('/admin/annotator/') || path.startsWith('/admin/applications');
    if (to === '/admin/clients') return path.startsWith('/admin/client/');
    if (to === '/admin/spl-teams') return path.startsWith('/admin/spl/');
    // Phase 5.75 Stage A — dataset detail route activates the Datasets sidebar item
    if (to === '/admin/datasets') return path.startsWith('/admin/dataset/');
    return false;
  };

  // Marketplace admin tools (Phase 2) only for Juuni-internal users.
  const isJuuniInternal = organization?.id === JUUNI_INTERNAL_ORG_ID || role === 'god';

  const navItems = [
    { to: '/admin', icon: '◈', label: 'Dashboard', end: true },
    { to: '/admin/projects', icon: '▤', label: 'Projects' },
    // Phase 5.75 Stage A — Deliverables catalog (formerly "Datasets" — UI renamed Phase 5.75-vocab; route + table stay `dataset` for DB compat)
    { to: '/admin/datasets', icon: '▣', label: 'Deliverables' },
    // Phase 6 — CSV ingest (admin + SPL both upload; page role-gates internally)
    { to: '/admin/ingest', icon: '↥', label: 'Ingest' },
    // Phase 8 — Digest approvals (admin-only; DigestApprovalPage hides for non-admin)
    { to: '/admin/digests', icon: '✉', label: 'Digests' },
    // Phase 5.75-vocab follow-up (2026-04-26): Applications was merged
    // into Annotators as a tab (?tab=applications). The pending-count
    // badge was removed 2026-04-27 — the in-page tab counter is enough,
    // sidebar badge was visual noise (pendingAppCount kept available via
    // the prop in case we want to surface it differently later).
    { to: '/admin/workforce', icon: '◫', label: 'Annotators' },
    // Track D dive #40 (FIND-017 fix, 2026-05-04): unified time approval
    // surface replaces the standalone "Flagged Time" page entry. The new
    // /admin/time-approval shows BOTH pending entries AND flagged entries
    // in one queue with a single bulk-approve action. Old
    // /admin/flagged-time-entries remains accessible via direct URL for
    // backward compat but isn't the primary nav target anymore.
    //
    // Phase 21.4 origin: SPLs had no sidebar entry point for the flagged
    // queue and had to type the URL directly — Maya/SPL daily-driver
    // surface should be one click away.
    { to: '/admin/time-approval', icon: '⚑', label: 'Time Approval' },
    { to: '/admin/clients', icon: '◇', label: 'Clients' },
    // Admin-only pages
    ...(isAdmin ? [
      { type: 'section', label: 'Management' },
      { to: '/admin/spl-teams', icon: '◉', label: 'SPLs' },
      { to: '/admin/financials', icon: '▥', label: 'Financials' },
      { to: '/admin/billing-settings', icon: '◍', label: 'Billing' },
      { to: '/admin/capacity', icon: '⊞', label: 'Capacity' },
    ] : []),
    // God-only Juuni-internal billing dashboard
    ...(role === 'god' ? [
      { type: 'section', label: 'Juuni Internal' },
      { to: '/god/billing', icon: '◈', label: 'Juuni Billing' },
    ] : []),
    { type: 'section', label: 'Analytics' },
    { to: '/admin/analytics', icon: '▦', label: 'Analytics' },
    { to: '/admin/reports', icon: '▤', label: 'Reports' },
    { to: '/admin/audit', icon: '◷', label: 'Audit Log' },
    ...(isJuuniInternal ? [
      { type: 'section', label: 'Marketplace' },
      { to: '/admin/marketplace-jobs', icon: '▧', label: 'Post Jobs' },
    ] : []),
  ];

  // Sidebar contents — same render tree at every viewport. The wrapping
  // chrome differs (inline `.admin-sidebar` rail at desktop, MobileDrawer
  // panel below md). Extracting the body keeps Rules-of-Hooks clean.
  const body = (
    <>
      {/* Logo — JUUNI platform brand. Uses var(--text-primary) so the
          brand automatically tracks the active theme (light or admin-dark).
          Japanese 十二 stripped from user-facing chrome per 2026-04-20
          product decision — identity lockup stays English-only in the app,
          Japanese reserved for the landing page atmospheric layer only.
          Subtitle below shows the user's company / organization name. */}
      <div style={{ padding: '4px 20px 16px', borderBottom: '1px solid var(--border-light)' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '0.22em', fontFamily: 'var(--font-sans)' }}>JUUNI</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)', letterSpacing: '0.02em' }}>
          {user?.user_metadata?.company || organization?.name || ''}
        </div>
      </div>

      <nav className="sidebar-nav" style={{ marginTop: 8 }}>
        {impersonating && (
          <div style={{ padding: '8px 14px', margin: '0 0 6px', fontSize: 12, fontWeight: 500, color: 'var(--text-warning)', background: 'var(--bg-warning)', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            role="button"
            tabIndex={0}
            onClick={() => { stopImpersonating(); navigate(role === 'god' ? '/god' : '/admin'); }}
            onKeyDown={activateOnEnterOrSpace(() => { stopImpersonating(); navigate(role === 'god' ? '/god' : '/admin'); })}>
            <span>Viewing as {impersonating.name}</span>
            <span style={{ fontSize: 12 }}>✕</span>
          </div>
        )}

        {navItems.map((item, i) => {
          if (item.type === 'section') {
            return (
              <div key={i} style={{ padding: '16px 12px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {item.label}
              </div>
            );
          }
          if (item.type === 'divider') return <div key={i} className="sidebar-divider" />;
          return (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `nav-item ${isActive || getExtraActive(item.to) ? 'active' : ''}`}>
              <span className="nav-icon">{item.icon}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span style={{
                    background: 'transparent', color: 'var(--text-error)', fontSize: 12,
                    fontFamily: 'var(--font-mono)', fontWeight: 600,
                    padding: '0 5px', borderRadius: 10, minWidth: 16,
                    height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, border: '1.5px solid var(--text-error)',
                  }}>{item.badge}</span>
                )}
              </span>
            </NavLink>
          );
        })}
      </nav>

      {/* Client Portal preview — SPL/admin/god can preview client view */}
      <div style={{ padding: '8px 12px 0' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Views</div>
        <a href="/client" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 12, color: 'var(--text-secondary)', textDecoration: 'none', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', marginBottom: 4, fontFamily: 'var(--font-sans)' }}>
          <span style={{ fontSize: 13 }}>◇</span>
          <span>Client Portal</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>↗</span>
        </a>
      </div>

      {/* Contact support — prefills org_id / role / page / UA in the email body
           so support@ can triage without asking for context */}
      <a href={buildSupportMailto({ orgId: organization?.id, role, path: location.pathname })}
         className="nav-item"
         style={{ marginBottom: 0, textDecoration: 'none' }}
         title="Email support — includes your session context">
        <span className="nav-icon">?</span>
        <span>Contact Support</span>
      </a>

      {/* Settings pinned above sign-out */}
      <NavLink to="/admin/settings" end
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        style={{ marginBottom: 0 }}>
        <span className="nav-icon">⚙</span>
        <span>Settings</span>
      </NavLink>

      <div style={{ padding: '12px 12px', borderTop: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg onClick={() => navigate('/admin/profile')} style={{ cursor: 'pointer', flexShrink: 0 }} title="Edit profile" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="5" r="2.5" stroke="var(--text-muted)" strokeWidth="1.3" />
            <path d="M3 14c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="var(--text-muted)" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <div style={{
            fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, cursor: 'pointer', transition: 'color 0.15s',
          }}
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin/profile')}
            onKeyDown={activateOnEnterOrSpace(() => navigate('/admin/profile'))}
            title="Edit profile"
            onMouseEnter={e => { e.currentTarget.style.color = '#3B6FD6'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}>
            {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
          </div>
          <button onClick={signOut} style={{
            fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', flexShrink: 0,
          }}>
            Sign out
          </button>
        </div>
      </div>
    </>
  );

  // Phase 14.2 — at md breakpoint and below, collapse to a hamburger
  // button that opens MobileDrawer (Radix Dialog). Above md, render
  // the inline 148px rail as before.
  if (isMobile) {
    const hamburger = (
      <button
        type="button"
        className="hamburger-button"
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
      >
        ☰
      </button>
    );
    return (
      <MobileDrawer trigger={hamburger} ariaLabel="Admin navigation">
        <div className="admin-sidebar">{body}</div>
      </MobileDrawer>
    );
  }

  return <div className="sidebar admin-sidebar">{body}</div>;
}
